import { Cloud, ContentCopy, Wifi } from '@mui/icons-material';
import { Box, CircularProgress, FormControlLabel, IconButton, Switch, Tooltip } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify, useRecordContext, useRefresh, useTranslate } from 'react-admin';

import { usePermission } from 'providers/auth/permissionProvider';
import { Unit, VPN } from 'types';

export type VPNConnectionMethod = 'mqtt' | 'direct';

const CONNECTING_POLL_INTERVAL = 2500;
const CONNECTED_POLL_INTERVAL = 15000;

/**
 * A unit can use the MQTT tunnel when MQTT is enabled, a valid SSH key is
 * registered and it is not a TT1 (TT1 never got MQTT support).
 */
export const supportsMqttTunnel = (record?: Unit): boolean =>
  !!record && !!record.mqtt_enabled && record.ssh_key_state === 'valid' && record.hardware_id !== 1;

/**
 * TT2 units that are still on CentOS 7 — every revision except `tt2-acd` — only have
 * the `iif` user, so the second hop has to target `iif@<vpn-ip>`. A null/unknown
 * revision (dev + staging) is treated as "not tt2-acd".
 */
export const buildSSHCommand = (record?: Unit, ipAddress?: string | null): string => {
  if (!record || !ipAddress) {
    return '';
  }

  const needsIifUser = record.hardware_id === 2 && record.hardware_revision_name !== 'tt2-acd';

  return `ssh -t admin@rsh.tover.care ssh ${needsIifUser ? 'iif@' : ''}${ipAddress}`;
};

/**
 * Inline VPN/SSH tunnel control rendered in the unit page status bar.
 *
 * It never connects or disconnects on its own: mounting the unit page only starts
 * status polling for an already-open tunnel, and unmounting (navigating away) only
 * stops that polling.
 */
const SSHTunnelControl = () => {
  const record = useRecordContext<Unit>();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const translate = useTranslate();
  const { hasPermission } = usePermission();

  const [chosenMethod, setChosenMethod] = useState<VPNConnectionMethod | null>(null);
  const [vpn, setVPN] = useState<VPN | undefined>(undefined);
  const [status, setStatus] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const pollTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const unitId = record?.id;
  const vpnId = record?.vpn_id;
  const mqttSupported = supportsMqttTunnel(record);
  // Only opening a Direct VPN tunnel is gated: `POST vpn` runs through
  // `VPNCreateRequest::authorize()` => `can('dev_options')`. Closing a tunnel
  // (`closeTerminal` / `DELETE vpn/{id}`) and the whole MQTT path are ungated, so an
  // already-open tunnel stays closable without the permission.
  const canOpenDirectVpn = hasPermission('dev_options');
  const preferredMethod: VPNConnectionMethod = chosenMethod ?? (mqttSupported ? 'mqtt' : 'direct');
  // An explicit choice can be invalidated by a refetch (an SSH-key reset or `mqtt_enabled`
  // being cleared elsewhere), so clamp it back to a usable method — otherwise the label and
  // the tunnel action would disagree. When neither method is usable the previous default is
  // kept, so an already-open tunnel still closes the way it was opened.
  const method: VPNConnectionMethod =
    preferredMethod === 'mqtt' && !mqttSupported
      ? 'direct'
      : preferredMethod === 'direct' && !canOpenDirectVpn && mqttSupported
        ? 'mqtt'
        : preferredMethod;
  const connected = status === 'connected';
  const noMethodAvailable = !mqttSupported && !canOpenDirectVpn;
  // Only render a switch when both methods are actually usable — with a single usable
  // method there is nothing to toggle, so the active mode is shown as plain text.
  const canChooseMethod = mqttSupported && canOpenDirectVpn;

  const stopPolling = useCallback(() => {
    if (pollTimeout.current) {
      clearTimeout(pollTimeout.current);
      pollTimeout.current = undefined;
    }
  }, []);

  useEffect(() => {
    if (!vpnId) {
      setVPN(undefined);
      setStatus('');
      stopPolling();
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const response: { data: VPN } = await dataProvider.get(`vpn/${vpnId}`);

        if (cancelled) {
          return;
        }

        setVPN(response.data);
        setStatus(response.data?.status ?? '');

        pollTimeout.current = setTimeout(
          poll,
          response.data?.status === 'connected' ? CONNECTED_POLL_INTERVAL : CONNECTING_POLL_INTERVAL,
        );
      } catch (error) {
        if (!cancelled) {
          setStatus('');
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [vpnId, dataProvider, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const openTunnel = async (via: VPNConnectionMethod) => {
    if (via === 'mqtt') {
      await dataProvider.post(`unit/${unitId}/startTerminal`, {});
      return;
    }

    // Legacy path: only create the VPN record — tover.tele picks it up on its next
    // status poll and dials in itself.
    await dataProvider.post('vpn', { unit_id: unitId });
  };

  const closeTunnel = async (via: VPNConnectionMethod) => {
    if (via === 'mqtt' && vpnId) {
      await dataProvider.delete('vpn', { id: vpnId });
      return;
    }

    await dataProvider.post(`unit/${unitId}/closeTerminal`, {});
  };

  const handleToggle = async () => {
    if (!record || busy) {
      return;
    }

    setBusy(true);

    try {
      if (vpnId) {
        await closeTunnel(method);
        // The polling effect is keyed on `vpnId`, which only drops after `refresh()` has
        // propagated the new record — so an already-scheduled poll would otherwise still fire
        // against the closed tunnel and flip the status back to "connected" for one interval.
        stopPolling();
        setVPN(undefined);
        setStatus('closed');
      } else {
        await openTunnel(method);
        setStatus('started');
      }

      refresh();
    } catch (error) {
      notify(vpnId ? 'resources.units.show.vpn.disconnect_failed' : 'resources.units.show.vpn.connect_failed', {
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleMethodChange = async (next: VPNConnectionMethod) => {
    // The `next === 'direct' && !canOpenDirectVpn` conjunct is unreachable from the switch —
    // it only renders when `canOpenDirectVpn` is true. Kept deliberately as defence in depth
    // for any future caller, so do not remove it to "cover" the branch.
    if (!record || busy || next === method || (next === 'direct' && !canOpenDirectVpn)) {
      return;
    }

    setBusy(true);

    try {
      // The ticket requires an active tunnel to be closed with the method that opened
      // it before the new method takes over.
      if (vpnId) {
        await closeTunnel(method);
        // Same reason as in `handleToggle`: clear the pending poll before `vpnId` changes, or
        // it resurrects the closed tunnel's status and IP for one poll interval.
        stopPolling();
        setVPN(undefined);
        setStatus('closed');
        refresh();
      }

      setChosenMethod(next);
    } catch (error) {
      notify('resources.units.show.vpn.switch_method_failed', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const copyCommand = async () => {
    await navigator.clipboard.writeText(buildSSHCommand(record, vpn?.ip_address));
    notify('resources.units.show.vpn.command_copied', { type: 'info' });
  };

  if (!record) {
    return null;
  }

  const mqttUnsupportedReason = mqttSupported
    ? ''
    : record.hardware_id === 1
      ? translate('resources.units.show.vpn.mqtt_unsupported_tt1')
      : !record.mqtt_enabled
        ? translate('resources.units.show.vpn.mqtt_disabled')
        : translate('resources.units.show.vpn.mqtt_no_ssh_key');

  const directUnavailableReason = canOpenDirectVpn ? '' : translate('resources.units.show.vpn.direct_no_permission');

  const unavailableReasons = [mqttUnsupportedReason, directUnavailableReason].filter(Boolean).join(' ');

  const sshCommand = buildSSHCommand(record, vpn?.ip_address);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, fontSize: '0.875rem' }}>
      {noMethodAvailable ? (
        // Neither method is usable, so a select with every option disabled would be dead
        // UI. Explain instead — an already-open tunnel stays observable and closable
        // below, because only *opening* a Direct VPN tunnel needs `dev_options`.
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {translate('resources.units.show.vpn.no_method_available')} {unavailableReasons}
        </Box>
      ) : canChooseMethod ? (
        // Both methods are usable: on = MQTT, off = Direct VPN. The visible label names the
        // field and follows the active method, while the accessible name stays stable across
        // flips via `inputProps` — phrased as an on/off statement about MQTT so that
        // "checked"/"unchecked" is meaningful on its own to a screen reader.
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={method === 'mqtt'}
              disabled={busy}
              onChange={(event) => handleMethodChange(event.target.checked ? 'mqtt' : 'direct')}
              inputProps={{ 'aria-label': translate('resources.units.show.vpn.method_switch_label') }}
            />
          }
          label={
            <>
              {translate('resources.units.show.vpn.method_label')}:{' '}
              {method === 'mqtt' ? (
                <Wifi fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
              ) : (
                <Cloud fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
              )}
              {translate(
                method === 'mqtt' ? 'resources.units.show.vpn.method_mqtt' : 'resources.units.show.vpn.method_direct',
              )}
            </>
          }
        />
      ) : (
        <>
          {/* Exactly one method is usable, so there is nothing to toggle. The shown mode is the
              clamped `method` — the same value `handleToggle` acts on — so a refetch that drops
              MQTT support can never leave a stale label behind or diverge from the action. */}
          <span>
            {translate('resources.units.show.vpn.method_label')}:{' '}
            <strong>
              {translate(
                method === 'mqtt' ? 'resources.units.show.vpn.method_mqtt' : 'resources.units.show.vpn.method_direct',
              )}
            </strong>
          </span>

          {!!unavailableReasons && (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {unavailableReasons}
            </Box>
          )}
        </>
      )}

      {(!noMethodAvailable || !!vpnId) && (
        <FormControlLabel
          control={<Switch size="small" checked={!!vpnId} disabled={busy} onChange={handleToggle} />}
          label={translate('resources.units.show.vpn.connect')}
        />
      )}

      {busy && <CircularProgress size={16} />}

      <span>
        {translate('resources.units.show.vpn.status')}:{' '}
        <strong>{status || translate('resources.units.show.vpn.disconnected')}</strong>
      </span>

      {!!vpnId && !connected && !busy && <CircularProgress size={16} />}

      {vpn?.ip_address && (
        <span>
          {translate('resources.units.show.vpn.ip_address')}: <strong>{vpn.ip_address}</strong>
        </span>
      )}

      {sshCommand && (
        <>
          <Box
            component="code"
            sx={{
              backgroundColor: 'grey.200',
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.75rem',
              wordBreak: 'break-all',
            }}
          >
            {sshCommand}
          </Box>
          <Tooltip title={translate('resources.units.show.vpn.copy_command')}>
            <IconButton
              size="small"
              onClick={copyCommand}
              aria-label={translate('resources.units.show.vpn.copy_command')}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default SSHTunnelControl;
